import { type GetRecoilValue, selector, waitForAll } from 'recoil';
import { Data } from 'effect';
import type { Layout } from 'react-grid-layout';
import type { Page } from 'venn-components';
import { PageType } from 'venn-utils';
import type { BlockId } from '../studio';
import { viewPages } from '../studio/configuration/print';
import { blockContinuationPageCount, blockContinuationSlices, blockSourcePageVisibleHeight } from './blockExpansion';
import { continuationUserBlocksState, isContinuationLayoutId, makeContinuationLayoutId } from './continuationLayoutId';

export interface ContinuationBlockLayout {
  readonly blockId: BlockId;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export type RenderedPage = Data.TaggedEnum<{
  SourcePage: { readonly sourceIndex: number; readonly page: Page };
  ContinuationPage: {
    readonly sourceBlockId: BlockId;
    readonly sourcePageIndex: number;
    readonly continuationIndex: number;
    readonly totalContinuations: number;
    readonly blockLayout: ContinuationBlockLayout;
    readonly sourcePageVisibleHeight: number;
    readonly sliceRowCount: number;
    readonly scrollOffsetPx: number;
    readonly sliceHeightPx: number;
    readonly sliceStartRowIndex: number;
    readonly sliceDataHeightPx: number;
    readonly userBlocks: Layout;
  };
}>;

export const RenderedPage = Data.taggedEnum<RenderedPage>();

export type SourcePageDescriptor = RenderedPage & { _tag: 'SourcePage' };
export type ContinuationPageDescriptor = RenderedPage & { _tag: 'ContinuationPage' };

/**
 * Continuation pages for every expanded block in `owners`, each block's pages in order. A block dropped onto one of
 * those pages can itself be expanded, so its own continuation pages follow the page hosting it.
 */
const buildContinuationPages = ({
  get,
  owners,
  sourcePageIndex,
  visited,
}: {
  get: GetRecoilValue;
  owners: Layout;
  sourcePageIndex: number;
  visited: Set<BlockId>;
}): RenderedPage[] => {
  const candidates = owners.filter((layout) => !isContinuationLayoutId(layout.i) && !visited.has(layout.i));
  candidates.forEach((layout) => visited.add(layout.i));

  const counts = get(waitForAll(candidates.map((layout) => blockContinuationPageCount(layout.i))));
  const expanded = candidates.flatMap((layout, idx) => {
    const totalContinuations = counts[idx] ?? 0;
    return totalContinuations > 0 ? [{ layout, totalContinuations }] : [];
  });

  const [sourceVisibleHeights, slicesByBlock] = get(
    waitForAll([
      waitForAll(expanded.map(({ layout }) => blockSourcePageVisibleHeight(layout.i))),
      waitForAll(expanded.map(({ layout }) => blockContinuationSlices(layout.i))),
    ]),
  );

  return expanded.flatMap(({ layout, totalContinuations }, idx) => {
    const blockId = layout.i;
    const sourcePageVisibleHeight = sourceVisibleHeights[idx] ?? 0;
    const slices = slicesByBlock[idx] ?? [];

    return Array.from({ length: totalContinuations }, (_, c) => {
      const slice = slices[c];
      const sliceRowCount = slice?.sliceRowCount ?? 1;
      const userBlocks = get(
        continuationUserBlocksState(makeContinuationLayoutId({ sourceBlockId: blockId, continuationIndex: c })),
      );

      return [
        RenderedPage.ContinuationPage({
          sourceBlockId: blockId,
          sourcePageIndex,
          continuationIndex: c,
          totalContinuations,
          blockLayout: { blockId, x: layout.x, y: layout.y, w: layout.w, h: sliceRowCount },
          sourcePageVisibleHeight,
          sliceRowCount,
          scrollOffsetPx: slice?.scrollOffsetPx ?? 0,
          sliceHeightPx: slice?.sliceHeightPx ?? 0,
          sliceStartRowIndex: slice?.sliceStartRowIndex ?? 0,
          sliceDataHeightPx: slice?.sliceDataHeightPx ?? 0,
          userBlocks,
        }),
        ...buildContinuationPages({ get, owners: userBlocks, sourcePageIndex, visited }),
      ];
    }).flat();
  });
};

export const renderedPagesState = selector<readonly RenderedPage[]>({
  key: 'renderedPages',
  get: ({ get }) => {
    const pages = get(viewPages);
    const visited = new Set<BlockId>();
    const result: RenderedPage[] = [];

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i]!;

      // Skip continuation pages already in viewPages — we derive them below.
      if (page.type === PageType.CONTINUATION) continue;

      result.push(RenderedPage.SourcePage({ sourceIndex: i, page }));

      if (page.type !== PageType.GRID) continue;

      result.push(...buildContinuationPages({ get, owners: page.layout, sourcePageIndex: i, visited }));
    }

    return result;
  },
});

export const renderedPageIndexBySourceIndexState = selector<ReadonlyMap<number, number>>({
  key: 'renderedPageIndexBySourceIndex',
  get: ({ get }) => {
    const rendered = get(renderedPagesState);
    const map = new Map<number, number>();
    rendered.forEach((entry, renderedIndex) => {
      if (RenderedPage.$is('SourcePage')(entry)) {
        map.set(entry.sourceIndex, renderedIndex);
      }
    });
    return map;
  },
});
